import { Box, FormControl, Grid, InputLabel, Typography } from "@material-ui/core";
import { useObserver } from "mobx-react-lite";
import React, { useRef, useState } from "react";
import { OutstandingPayment } from "Custom/Models/DashboardModels/outstandingPayment";

// Core
import { isEmptyOrWhitespace } from "Core/Utils/Utils";

// Custom
import { EditableInput } from "Custom/Components/Edits/EditableInput";
import { BuyModelDTO, UpdateStripePaymentRequestDTO } from "Custom/Models/BuyModel";
import { BuyViewModel } from "Custom/ViewModels/BuyViewModel";
import { CardPaymentViewModel } from "Custom/ViewModels/CardPaymentViewModel";
import { CardPaymentStyles } from "./CardPayment.styling";
import { StandardButton } from "../Buttons/StandardButton";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { WorldpayCardPayment, WorldpayCardPaymentRef } from "./WorldpayCardPayment";
import { StoresInstance } from "Custom/Stores";
import { Modal } from "@material-ui/core";

interface IProps {
    totalPrice: number;
    propertyToProcessId: string;
    reservationRequestId: string | undefined;
    orderRequestId: string | undefined;
    currentUserId: string | undefined;
    onCancel: () => void; // Kept for backward compatibility
    outstandingPayments: OutstandingPayment[] | null;
}

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: "#32325d",
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
                color: "#aab7c4",
            },
        },
        invalid: {
            color: "#fa755a",
            iconColor: "#fa755a",
        },
    },
    labels: {
        postalCode: "Postcode",
    },
    hidePostalCode: true,
};

export const CardPayment: React.FC<IProps> = ({ totalPrice, propertyToProcessId, reservationRequestId, orderRequestId, currentUserId, outstandingPayments }) => {
    const [viewModel] = React.useState(() => new CardPaymentViewModel());
    const [buyViewModel] = React.useState(() => BuyViewModel.Instance);
    const bind = viewModel.getContext();
    const cardPaymentStyles = CardPaymentStyles();
    const [processing, setProcessing] = useState(false);
    const [cardError, setCardError] = useState<string | undefined>(undefined);
    const formRef = useRef<HTMLFormElement>(null);
    const worldpayRef = useRef<WorldpayCardPaymentRef>(null);
    const challengeRef = useRef<HTMLDivElement>(null);
    const [showChallenge, setShowChallenge] = useState(false);

    // Stripe hooks
    const stripe = useStripe();
    const elements = useElements();

    React.useEffect(() => {
        buyViewModel.setErrorMessage("");
    }, []);

    const handleCardChange = (event: any) => {
        setCardError(event.error ? event.error.message : "");
        viewModel.setCardComplete(event.complete); // update card details to be validated
    };

    const handleWorldpayFormValidChange = (valid: boolean) => {
        viewModel.setCardComplete(valid);
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!challengeRef.current) return;

        if (viewModel.stripePaymentProvider && (!stripe || !elements)) {
            // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
            return;
        }

        setProcessing(true);

        // set the paymentDTO
        const requests: UpdateStripePaymentRequestDTO[] = [];
        if (reservationRequestId) {
            const reservationRequest: UpdateStripePaymentRequestDTO = {
                id: reservationRequestId,
                paidByIdentityUserId: currentUserId ? currentUserId : "",
            };
            requests.push(reservationRequest);
        }
        if (orderRequestId) {
            const reservationRequest: UpdateStripePaymentRequestDTO = {
                id: orderRequestId,
                paidByIdentityUserId: currentUserId ? currentUserId : "",
            };
            requests.push(reservationRequest);
        }

        const model: BuyModelDTO = {
            name: viewModel.model.name,
            propertyId: propertyToProcessId,
            updatedStripePaymentRequests: requests,
            paymentType: "card",
            price: totalPrice,
            paymentSuccess: false,
            actionUrl: "",
            intentId: "",
            paymentMethodId: "",
            chosenOutstandingPayments: outstandingPayments ? outstandingPayments : [],
        };

        let cardElement: any = null;
        if (viewModel.stripePaymentProvider) {
            // Get the card element
            cardElement = elements.getElement(CardElement);
            if (!cardElement) {
                setProcessing(false);
                setCardError("Card element not found");
                return;
            }

            const result = await stripe!.createPaymentMethod({
                type: "card",
                card: cardElement,
                billing_details: {
                    name: model.name,
                },
            });

            if (result.paymentMethod) {
                model.paymentMethodId = result.paymentMethod.id;
            }
        } else {
            const session = await worldpayRef.current?.generateSessionState();
            if (!session) {
                setProcessing(false);
                setCardError("Failed to generate Worldpay session");
                return;
            }

            model.worldpaySession = session;
            model.lang = navigator.language;
            model.javaEnabled = navigator.javaEnabled ? navigator.javaEnabled() : false; // by spec this is always false, but lets be safe
            model.colorDepth = window.screen.colorDepth;
            model.screenHeight = window.screen.height;
            model.screenWidth = window.screen.width;
            model.timeZoneOffset = new Date().getTimezoneOffset();
        }

        await buyViewModel.cardPayment(model, (show: boolean) => setShowChallenge(show), challengeRef.current);
        setProcessing(false);
    };

    // make sure the form ID doesn't change, its referenced by Worldpay
    return useObserver(() => (
        <form id="card-payment-checkout" ref={formRef} className={cardPaymentStyles.paymentForm} onSubmit={onSubmit}>
            <Grid container spacing={2}>
                <Grid item md={12} sm={12} xs={12}>
                    <Box>
                        <EditableInput
                            fieldName={bind.name}
                            inputProps={{
                                autoComplete: "cc-name",
                                id: "card-input-name",
                                placeholder: "Name on card",
                                InputProps: {
                                    notched: false,
                                },
                                InputLabelProps: {
                                    shrink: true,
                                },
                            }}
                            label="Name on card"
                            validateOnBlur={false}
                            viewModel={viewModel}
                        />
                    </Box>
                </Grid>
                <Grid item md={12} sm={12} xs={12}>
                    <Box style={{ padding: "10px 0" }}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel shrink>Card Information</InputLabel>
                            {viewModel.stripePaymentProvider ? (
                                <div
                                    style={{
                                        padding: "10px",
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        backgroundColor: "#fff",
                                    }}
                                >
                                    <CardElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
                                </div>
                            ) : (
                                <WorldpayCardPayment
                                    ref={worldpayRef}
                                    formRef={formRef}
                                    testEnvironment={StoresInstance.domain.AccountStore.WorldpayTestEnvironment}
                                    checkoutId={StoresInstance.domain.AccountStore.WorldpayCheckoutId}
                                    renderLoading={() => (
                                        <Grid item xs={12}>
                                            <p style={{ textAlign: "center" }}>Loading...</p>
                                        </Grid>
                                    )}
                                    onFormValidChange={handleWorldpayFormValidChange}
                                />
                            )}
                            {cardError && (
                                <Typography variant="body2" style={{ color: "red", marginTop: "8px" }}>
                                    {cardError}
                                </Typography>
                            )}
                        </FormControl>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Grid spacing={2} container>
                        {!isEmptyOrWhitespace(buyViewModel.errorMessage) && (
                            <Grid item xs={12}>
                                <p style={{ color: "red", textAlign: "center" }}>{buyViewModel.errorMessage}</p>
                            </Grid>
                        )}
                        {(buyViewModel.IsLoading || processing) && (
                            <Grid item xs={12}>
                                <p style={{ textAlign: "center" }}>Please stand by...</p>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <StandardButton
                                canExecute={
                                    !buyViewModel.IsLoading &&
                                    !processing &&
                                    viewModel.areAllRequiredFieldsFilled() &&
                                    !cardError &&
                                    outstandingPayments !== null &&
                                    outstandingPayments.length > 0
                                }
                                className={cardPaymentStyles.payNowButton}
                                displayName={processing ? "Processing..." : "Pay now"}
                                isSubmit={true}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Modal open={showChallenge} keepMounted disableBackdropClick disableEscapeKeyDown>
                <div style={{ display: "grid", placeItems: "center", width: "100%", height: "100%" }} ref={challengeRef}></div>
            </Modal>
        </form>
    ));
};
